import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Link as RouterLink, usePathname, useRouter, Href } from 'expo-router';
import { MenuIcon, X, SquarePen } from 'lucide-react-native';
import { forwardRef, useState } from 'react';
import { ButtonProps } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { TrackedLink as TrackedRouterLink } from './TrackedLink';

import { primum_default_avatar } from '@/assets/images';
import { Avatar, AvatarImage } from '@/components//ui/avatar';
import { Button, ButtonIcon, ButtonText } from '@/components//ui/button';
import { AwarenessLogo } from '@/components/AwarenessLogo';
import { SupportForm } from '@/components/supportForm';
import { Box } from '@/components/ui/box';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Menu, MenuItem, MenuItemLabel, MenuSeparator } from '@/components/ui/menu';
import { NewBadge } from '@/components/ui/new-badge';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { useBreakpointValue } from '@/components/ui/utils/use-break-point-value';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { NAV_LABELS } from '@/constants/navigation';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useFellowsNavBadge } from '@/hooks/useFellowsNavBadge';
import { useResumableSurveys } from '@/features/surveys';
import { usePollHistory } from '@/features/polls';
import {
  useCurrentPagePath,
  useRedirectPath,
  buildLoginPathWithRedirect,
  buildSignupPathWithRedirect,
} from '@/hooks/useRedirectPath';
import { useSupportPrompt } from '@/features/support-contact';
import { useSupportForm } from '@/hooks/useSupportForm';
import { useUnreadCounts } from '@/hooks/useUnreadCounts';
import { describeResumableSurveys } from '@/util/accountBadgeLabels';
import { segmentClient } from '@/util/analytics';
import { trackPollHistoryViewed } from '@/util/analyticsEvents';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

type NavBarItemProps = Partial<ButtonProps> & {
  href?: string;
  children: React.ReactNode;
  /**
   * Rendered beside the label rather than inside it, so a badge is never nested
   * in the label's text node. Sits within the link, so it shares the item's
   * press target instead of being a dead spot next to it.
   */
  badge?: React.ReactNode;
};

/**
 * Shows the short label below 1152px, where the full-width authenticated bar
 * crowds the logo and pushes the account avatar past the edge. The full label
 * stays in the accessible name.
 */
const ShortLabel = ({ full, short }: { full: string; short: string }) => (
  <>
    <span className="min-[1152px]:hidden" aria-hidden>
      {short}
    </span>
    <span className="sr-only min-[1152px]:not-sr-only">{full}</span>
  </>
);

const NavBarItem = forwardRef<ButtonProps, NavBarItemProps>(
  ({ children, badge, ...props }, ref) => {
    return (
      <Button variant="link" className="gap-1.5" {...props} ref={ref}>
        <ButtonText
          className={twJoin(
            'font-normal text-sm text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-200'
          )}>
          {children}
        </ButtonText>
        {badge}
      </Button>
    );
  }
);

/**
 * The nav's account avatar, dotted while the user has a survey left to finish.
 *
 * The dot repeats the marker the mobile Account tab already wears for the same
 * reason, so one pending survey reads the same wherever the user meets it. It
 * is ringed in the nav's own field so it stays a dot over a photo avatar rather
 * than melting into whatever the picture happens to be.
 */
function AccountAvatar({
  source,
  size,
  showSurveyBadge,
}: {
  source: React.ComponentProps<typeof AvatarImage>['source'];
  size: 'sm' | 'md';
  showSurveyBadge: boolean;
}) {
  return (
    <Box className="relative">
      <Avatar size={size}>
        <AvatarImage source={source} />
      </Avatar>

      {showSurveyBadge ? (
        <Box className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-primary-500 bg-tertiary-400" />
      ) : null}
    </Box>
  );
}

const LOGO_MOBILE_SIZE = { height: 24, width: 192 };

const LOGO_DESKTOP_SIZE = { height: 24, width: 192 };

export function NavBar() {
  const { navigate, replace } = useRouter();

  const pathname = usePathname();

  const currentPagePath = useCurrentPagePath();

  const { redirectPath } = useRedirectPath();

  const { currentUser, needsNpiVerification, needsAccountDetailsConfirmation, isUnauthenticated } =
    useAuthStatus();

  const handleLogout = () => {
    replace('/logout');
  };

  const profile = currentUser && getProfileNameAndInitials(currentUser);

  const { hasAnyUnread: hasUnreadMessages } = useUnreadCounts();

  const [isOpen, setIsOpen] = useState(false);

  const supportPrompt = useSupportPrompt({ source: 'nav_bar' });

  /**
   * Fellows Corner nav link visibility. Gated by the FELLOWS_CORNER flag so it
   * shows persistently once enabled for the user's environment / cohort, rather
   * than needing a per-session opt-in.
   */
  const isFellowsCornerEnabled = useFeatureFlag(FEATURE_FLAGS.FELLOWS_CORNER);

  const showFellowsNewBadge = useFellowsNavBadge();

  const { badgeCount: resumableSurveyCount } = useResumableSurveys();

  const pendingSurveysAnnouncement = describeResumableSurveys(resumableSurveyCount);

  const hasResumableSurveys = !!pendingSurveysAnnouncement;

  const { polls: answeredPolls } = usePollHistory();

  const supportForm = useSupportForm({
    initialValues: {
      name: profile?.name || '',
      reason: 'Account Access',
    },
    userData: {
      npi: currentUser?.npiNumber ?? '',
      institution: currentUser?.institution ?? '',
      state: currentUser?.state ?? '',
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      identifiesAsDoctor: currentUser?.identifiesAsDoctor,
    },
  });

  const toggleVisibility = () => {
    setIsOpen((prev) => !prev);
  };

  /*
   * A bounce onto a public-twin page (e.g. `/directory?redirect_path=/community/55`)
   * already carries the real destination in that query param. Preferring it over the raw
   * current URL matters because `redirect_path` is itself in the sensitive-param denylist
   * — a nesting guard against the value compounding on every pass — so building from
   * `currentPagePath` there strips the destination right back out.
   */
  const destinationForAuthLinks = redirectPath ?? currentPagePath;

  const handleLoginClick = () => {
    const loginPath = destinationForAuthLinks
      ? buildLoginPathWithRedirect(destinationForAuthLinks)
      : '/login';

    navigate(loginPath as Href);
  };

  const handleSignupClick = () => {
    const signupPath = destinationForAuthLinks
      ? buildSignupPathWithRedirect(destinationForAuthLinks)
      : '/create-account/step-1';

    navigate(signupPath as Href);
  };

  const imageSize =
    useBreakpointValue({
      default: LOGO_MOBILE_SIZE,
      md: LOGO_DESKTOP_SIZE,
    }) || LOGO_MOBILE_SIZE;

  const onNpiVerificationPage = pathname.includes('/npi-verification');

  /**
   * Where the logo goes, which is not always the directory.
   *
   * A user parked on the account-details gate is sent back to it by the secure layout,
   * so pointing the logo at the directory offers a way out that does not exist. It names
   * the gate instead, which leaves the mark clickable and inert rather than appearing to
   * navigate and bouncing.
   */
  const buildLogoHref = () => {
    if (needsAccountDetailsConfirmation) {
      return '/(secure)/confirm-account-details' as const;
    }

    return needsNpiVerification ? ('/(open)/directory' as const) : ('/directory' as const);
  };

  /**
   * The nav a user gets while parked on a gate screen: an account menu and nothing else.
   *
   * Every other item in this bar names a secure route, and the secure layout bounces a
   * user who has not confirmed their details back to the gate. Offering the link anyway
   * is offering an action that cannot succeed, and until the gate's redirect was
   * hardened it was also how the screen was crashed.
   *
   * Sign out stays reachable, so a user who cannot or will not confirm is not stuck with
   * no way out.
   */
  const renderParkedNavItems = () => (
    <Menu
      placement="bottom right"
      offset={5}
      trigger={(props: Record<string, unknown>) => (
        <Pressable {...props} aria-label="account">
          <Avatar size="sm">
            <AvatarImage source={getAvatarUrl(currentUser?.avatar) || primum_default_avatar} />
          </Avatar>
        </Pressable>
      )}>
      <MenuItem key="ContactSupport" textValue="Contact Support" onPress={supportPrompt.open}>
        <MenuItemLabel>Contact Support</MenuItemLabel>
      </MenuItem>

      <MenuSeparator />

      <MenuItem key="Sign out" textValue="Sign out" onPress={handleLogout}>
        <MenuItemLabel>Sign out</MenuItemLabel>
      </MenuItem>
    </Menu>
  );

  const renderDesktopNavItems = () => {
    if (needsNpiVerification) {
      return (
        <>
          <RouterLink href="/(open)/directory" asChild>
            <NavBarItem>{NAV_LABELS.directory.webNav}</NavBarItem>
          </RouterLink>

          <RouterLink href="/(open)/community" asChild>
            <NavBarItem>{NAV_LABELS.community.webNav}</NavBarItem>
          </RouterLink>

          <RouterLink href="/(open)/content-library" asChild>
            <NavBarItem>{NAV_LABELS.contentLibrary.webNav}</NavBarItem>
          </RouterLink>

          <TrackedRouterLink
            href="/inquiry"
            asChild
            trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
            trackingData={{ source: 'web_navbar_unverified' }}>
            <Button
              size="sm"
              variant="solid"
              action="primary"
              className="border border-typography-0 data-[hover=true]:bg-typography-0 data-[active=true]:bg-background-50">
              <ButtonIcon
                as={SquarePen}
                className="text-typography-0 group-hover/button:text-typography-900 group-active/button:text-typography-900"
              />
              <ButtonText className="font-semibold text-xs text-typography-0 group-hover/button:text-typography-900 group-active/button:text-typography-900">
                {NAV_LABELS.inquiry.webNav}
              </ButtonText>
            </Button>
          </TrackedRouterLink>

          {!onNpiVerificationPage && (
            <RouterLink href="/(secure)/npi-verification" asChild>
              <Button size="sm" variant="solid" className="bg-typography-0 leading-none">
                <ButtonText className="text-xs leading-none text-primary-800">
                  Complete Verification
                </ButtonText>
              </Button>
            </RouterLink>
          )}

          <Menu
            placement="bottom right"
            offset={5}
            trigger={(props: Record<string, unknown>) => (
              <Pressable {...props} aria-label="account">
                <Avatar size="sm">
                  <AvatarImage
                    source={getAvatarUrl(currentUser?.avatar) || primum_default_avatar}
                  />
                </Avatar>
              </Pressable>
            )}>
            <MenuItem key="ContactSupport" textValue="Contact Support" onPress={supportPrompt.open}>
              <MenuItemLabel>Contact Support</MenuItemLabel>
            </MenuItem>

            <MenuSeparator />

            <MenuItem key="Sign out" textValue="Sign out" onPress={handleLogout}>
              <MenuItemLabel>Sign out</MenuItemLabel>
            </MenuItem>
          </Menu>
        </>
      );
    }

    if (needsAccountDetailsConfirmation) {
      return renderParkedNavItems();
    }

    if (!isUnauthenticated) {
      return (
        <>
          <RouterLink href="/directory" asChild>
            <NavBarItem>{NAV_LABELS.directory.webNav}</NavBarItem>
          </RouterLink>

          <RouterLink href="/inbox" asChild>
            <NavBarItem>
              <>
                {NAV_LABELS.inbox.webNav}
                {hasUnreadMessages && (
                  <VStack className="absolute -right-1.5 top-0 h-2 w-2 rounded-full bg-tertiary-400 min-[1152px]:-right-3" />
                )}
              </>
            </NavBarItem>
          </RouterLink>

          <RouterLink href="/community" asChild>
            <NavBarItem>{NAV_LABELS.community.webNav}</NavBarItem>
          </RouterLink>

          <RouterLink href="/content-library" asChild>
            <NavBarItem>{NAV_LABELS.contentLibrary.webNav}</NavBarItem>
          </RouterLink>

          {isFellowsCornerEnabled ? (
            <TrackedRouterLink
              href="/fellows"
              asChild
              trackingEvent={ANALYTICS_EVENTS.FELLOWS_NAV_ITEM_CLICKED}
              trackingData={{ source: 'web_navbar_authenticated' }}>
              <NavBarItem badge={showFellowsNewBadge ? <NewBadge surface="brand" /> : null}>
                {NAV_LABELS.fellows.webNav}
              </NavBarItem>
            </TrackedRouterLink>
          ) : null}

          <HStack className="items-center gap-2.5 min-[1152px]:gap-3">
            <TrackedRouterLink
              href="/account/invites"
              asChild
              trackingEvent={ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED}
              trackingData={{ source: 'web_navbar_authenticated' }}>
              <Button
                size="md"
                variant="outline"
                className="data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100">
                <ButtonText className="font-bold text-xs text-typography-0 group-hover/button:text-primary-700 group-active/button:text-typography-900">
                  <ShortLabel full={NAV_LABELS.invites.webNav} short="Invite" />
                </ButtonText>
              </Button>
            </TrackedRouterLink>

            <TrackedRouterLink
              href="/inquiry"
              asChild
              trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
              trackingData={{ source: 'web_navbar_authenticated' }}>
              <Button size="sm" variant="solid" className="bg-typography-0">
                <Icon
                  as={SquarePen}
                  className="text-primary-700 group-hover/button:text-typography-0"
                />
                <ButtonText className="font-bold text-xs leading-none text-primary-700">
                  <ShortLabel full={NAV_LABELS.inquiry.webNav} short="Ask" />
                </ButtonText>
              </Button>
            </TrackedRouterLink>

            <VStack className="min-[1152px]:mx-1" />

            <Menu
              placement="bottom right"
              offset={5}
              trigger={(props: Record<string, unknown>) => (
                <Pressable
                  {...props}
                  aria-label={
                    pendingSurveysAnnouncement
                      ? `account, ${pendingSurveysAnnouncement}`
                      : 'account'
                  }>
                  <AccountAvatar
                    source={getAvatarUrl(currentUser?.avatar) || primum_default_avatar}
                    size="sm"
                    showSurveyBadge={hasResumableSurveys}
                  />
                </Pressable>
              )}>
              <MenuItem
                key="AccountSettings"
                textValue="Account Settings"
                aria-label={
                  pendingSurveysAnnouncement
                    ? `${NAV_LABELS.account.webNav}, ${pendingSurveysAnnouncement}`
                    : undefined
                }
                onPress={() => navigate('/account')}>
                <MenuItemLabel>{NAV_LABELS.account.webNav}</MenuItemLabel>

                {hasResumableSurveys ? (
                  <Box className="ml-2 h-2 w-2 rounded-full bg-tertiary-400" />
                ) : null}
              </MenuItem>

              <MenuSeparator />

              <MenuItem
                key="ProfileDetails"
                textValue="Profile"
                onPress={() => {
                  segmentClient.track(ANALYTICS_EVENTS.PROFILE_VIEWED, {
                    source: 'navbar_menu',
                  });

                  navigate('/profile/me');
                }}>
                <MenuItemLabel>Profile</MenuItemLabel>
              </MenuItem>

              <MenuItem
                key="ContactDetails"
                textValue="Contact Details"
                onPress={() => navigate('/account/contact-details')}>
                <MenuItemLabel>Contact Details</MenuItemLabel>
              </MenuItem>

              <MenuItem
                key="NotificationSettings"
                textValue="Notification Settings"
                onPress={() => navigate('/account/notification-settings')}>
                <MenuItemLabel>Notification Settings</MenuItemLabel>
              </MenuItem>

              {/* An array rather than null, so the menu's item collection never meets an empty child. */}
              {answeredPolls.length > 0
                ? [
                    <MenuItem
                      key="YourPolls"
                      textValue="Your Polls"
                      onPress={() => {
                        trackPollHistoryViewed({ source: 'web_navbar_menu' });

                        navigate(ROUTES.ACCOUNT_POLLS);
                      }}>
                      <MenuItemLabel>Your Polls</MenuItemLabel>
                    </MenuItem>,
                  ]
                : []}

              <MenuItem
                key="InviteAPeer"
                textValue="Invite a Peer"
                onPress={() => {
                  segmentClient.track(ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED, {
                    source: 'web_navbar_menu',
                  });

                  navigate('/account/invites');
                }}>
                <MenuItemLabel>{NAV_LABELS.invites.webNav}</MenuItemLabel>
              </MenuItem>

              <MenuItem
                key="ContactSupport"
                textValue="Contact Support"
                onPress={supportPrompt.open}>
                <MenuItemLabel>Contact Support</MenuItemLabel>
              </MenuItem>

              <MenuSeparator />

              <MenuItem key="Sign out" textValue="Sign out" onPress={handleLogout}>
                <MenuItemLabel>Sign out</MenuItemLabel>
              </MenuItem>
            </Menu>
          </HStack>
        </>
      );
    }

    return (
      <>
        <RouterLink href="/(open)/directory" asChild>
          <NavBarItem>{NAV_LABELS.directory.webNav}</NavBarItem>
        </RouterLink>

        <RouterLink href="/(open)/community" asChild>
          <NavBarItem>{NAV_LABELS.community.webNav}</NavBarItem>
        </RouterLink>

        <RouterLink href="/(open)/content-library" asChild>
          <NavBarItem>{NAV_LABELS.contentLibrary.webNav}</NavBarItem>
        </RouterLink>

        <TrackedRouterLink
          href="/inquiry"
          asChild
          trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
          trackingData={{ source: 'web_navbar_unauthenticated' }}>
          <Button
            size="sm"
            variant="solid"
            action="primary"
            className="border border-typography-0 data-[hover=true]:bg-typography-0 data-[active=true]:bg-background-50">
            <ButtonIcon
              as={SquarePen}
              className="text-typography-0 group-hover/button:text-typography-900 group-active/button:text-typography-900"
            />
            <ButtonText className="text-xs text-typography-0 group-hover/button:text-typography-900 group-active/button:text-typography-900">
              {NAV_LABELS.inquiry.webNav}
            </ButtonText>
          </Button>
        </TrackedRouterLink>

        <Button
          variant="solid"
          size="sm"
          action="primary"
          onPress={handleLoginClick}
          className="border border-typography-0 data-[hover=true]:bg-typography-0 data-[active=true]:bg-background-50">
          <ButtonText className="text-xs text-typography-0 group-hover/button:text-typography-900 group-active/button:text-typography-900">
            Log in
          </ButtonText>
        </Button>

        <Button
          variant="solid"
          size="sm"
          action="primary"
          onPress={handleSignupClick}
          className="bg-typography-0 data-[hover=true]:bg-primary-600 data-[active=true]:bg-primary-700">
          <ButtonText className="text-xs text-typography-900 group-hover/button:text-typography-0 group-active/button:text-typography-0">
            Sign up
          </ButtonText>
        </Button>
      </>
    );
  };

  const renderMobileNavItems = () => {
    if (needsNpiVerification) {
      return (
        <>
          <VStack space="xs" className="my-3 items-start px-4">
            <RouterLink href="/(open)/directory" asChild>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.directory.webNav}</NavBarItem>
            </RouterLink>

            <RouterLink href="/(open)/community" asChild>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.community.webNav}</NavBarItem>
            </RouterLink>

            <RouterLink href="/(open)/content-library" asChild>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.contentLibrary.webNav}</NavBarItem>
            </RouterLink>

            <TrackedRouterLink
              href="/inquiry"
              asChild
              trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
              trackingData={{ source: 'web_mobile_menu_unverified' }}>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.inquiry.webNav}</NavBarItem>
            </TrackedRouterLink>

            {!onNpiVerificationPage && (
              <RouterLink href="/(secure)/npi-verification" asChild>
                <NavBarItem onPress={toggleVisibility}>Complete Verification</NavBarItem>
              </RouterLink>
            )}

            <NavBarItem onPress={handleLogout}>Sign out</NavBarItem>
          </VStack>
        </>
      );
    }

    if (needsAccountDetailsConfirmation) {
      return (
        <VStack space="xs" className="my-3 items-start px-4">
          <NavBarItem onPress={supportPrompt.open}>Contact Support</NavBarItem>

          <NavBarItem onPress={handleLogout}>Sign out</NavBarItem>
        </VStack>
      );
    }

    if (!isUnauthenticated) {
      return (
        <>
          <RouterLink
            href="/account"
            aria-label={
              pendingSurveysAnnouncement
                ? `${profile?.name || NAV_LABELS.account.webNav}, ${pendingSurveysAnnouncement}`
                : undefined
            }>
            <HStack className="items-center justify-start py-6" space="xl">
              <AccountAvatar
                source={getAvatarUrl(currentUser?.avatar) || primum_default_avatar}
                size="md"
                showSurveyBadge={hasResumableSurveys}
              />

              <Text className="text-typography-0">{profile?.name}</Text>
            </HStack>
          </RouterLink>

          <VStack space="xs" className="my-3 items-start px-4">
            <RouterLink href="/directory" asChild>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.directory.webNav}</NavBarItem>
            </RouterLink>

            <RouterLink href="/inbox" asChild>
              <NavBarItem onPress={toggleVisibility}>
                <>
                  {NAV_LABELS.inbox.webNav}
                  {hasUnreadMessages ? (
                    <VStack className="absolute -right-3 top-0 h-3 w-3 rounded-full bg-tertiary-400" />
                  ) : null}
                </>
              </NavBarItem>
            </RouterLink>

            <RouterLink href="/community" asChild className="text-left">
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.community.webNav}</NavBarItem>
            </RouterLink>

            <RouterLink href="/content-library" asChild className="text-left">
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.contentLibrary.webNav}</NavBarItem>
            </RouterLink>

            {isFellowsCornerEnabled ? (
              <TrackedRouterLink
                href="/fellows"
                asChild
                className="text-left"
                trackingEvent={ANALYTICS_EVENTS.FELLOWS_NAV_ITEM_CLICKED}
                trackingData={{ source: 'web_mobile_menu' }}>
                <NavBarItem
                  onPress={toggleVisibility}
                  badge={showFellowsNewBadge ? <NewBadge surface="brand" /> : null}>
                  {NAV_LABELS.fellows.webNav}
                </NavBarItem>
              </TrackedRouterLink>
            ) : null}

            <TrackedRouterLink
              href="/inquiry"
              asChild
              trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
              trackingData={{ source: 'web_mobile_menu' }}>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.inquiry.webNav}</NavBarItem>
            </TrackedRouterLink>

            <TrackedRouterLink
              href="/account/invites"
              asChild
              className="text-left"
              trackingEvent={ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED}
              trackingData={{ source: 'web_mobile_menu' }}>
              <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.invites.webNav}</NavBarItem>
            </TrackedRouterLink>

            <RouterLink href="/account" asChild className="text-left">
              <NavBarItem
                onPress={toggleVisibility}
                aria-label={
                  pendingSurveysAnnouncement
                    ? `${NAV_LABELS.account.webNav}, ${pendingSurveysAnnouncement}`
                    : undefined
                }
                badge={
                  hasResumableSurveys ? (
                    <Box className="h-2 w-2 rounded-full bg-tertiary-400" />
                  ) : null
                }>
                {NAV_LABELS.account.webNav}
              </NavBarItem>
            </RouterLink>

            <NavBarItem onPress={handleLogout}>Sign out</NavBarItem>
          </VStack>
        </>
      );
    }

    return (
      <>
        <RouterLink href="/(open)/directory" asChild>
          <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.directory.webNav}</NavBarItem>
        </RouterLink>

        <RouterLink href="/(open)/community" asChild>
          <NavBarItem onPress={toggleVisibility}>Inquiry Library</NavBarItem>
        </RouterLink>

        <RouterLink href="/(open)/content-library" asChild>
          <NavBarItem onPress={toggleVisibility}>Content Library</NavBarItem>
        </RouterLink>

        <TrackedRouterLink
          href="/inquiry"
          asChild
          trackingEvent={ANALYTICS_EVENTS.SEND_INQUIRY_CLICKED}
          trackingData={{ source: 'web_mobile_menu_unauthenticated' }}>
          <NavBarItem onPress={toggleVisibility}>{NAV_LABELS.inquiry.webNav}</NavBarItem>
        </TrackedRouterLink>

        <NavBarItem
          onPress={() => {
            toggleVisibility();

            handleLoginClick();
          }}>
          Sign in
        </NavBarItem>

        <NavBarItem
          onPress={() => {
            toggleVisibility();

            handleSignupClick();
          }}>
          Sign up
        </NavBarItem>
      </>
    );
  };

  return (
    <VStack
      data-navbar="true"
      className={twJoin(
        'relative z-10 min-h-fit bg-primary-500',
        isWeb && 'border-b border-background-0'
      )}>
      <HStack className={twJoin('w-full items-center justify-between px-4 py-3 lg:px-8')}>
        <RouterLink asChild href={buildLogoHref()}>
          <Pressable>
            <AwarenessLogo alt="Primum logo with mark" kind="reverse" box={imageSize} />
          </Pressable>
        </RouterLink>

        <HStack className="hidden items-center gap-5 lg:flex min-[1152px]:gap-7">
          {renderDesktopNavItems()}
        </HStack>

        <HStack className="lg:hidden">
          <Pressable onPress={toggleVisibility} className="flex h-9 items-center justify-center">
            <Icon as={isOpen ? X : MenuIcon} size="xl" className="text-typography-0" />
          </Pressable>
        </HStack>
      </HStack>

      {isOpen ? (
        <VStack className="absolute left-0 right-0 top-full h-screen items-end justify-start overflow-hidden bg-primary-500 lg:hidden lg:px-10">
          <Divider />

          <VStack className="w-full items-start px-4 py-3">{renderMobileNavItems()}</VStack>
        </VStack>
      ) : null}

      {supportPrompt.isFormOpen && (
        <SupportForm
          isOpen={supportPrompt.isFormOpen}
          onClose={supportPrompt.closeForm}
          npi={currentUser?.npiNumber ?? ''}
          institution={currentUser?.institution ?? ''}
          state={currentUser?.state ?? ''}
          firstName={currentUser?.firstName ?? ''}
          lastName={currentUser?.lastName ?? ''}
          identifiesAsDoctor={currentUser?.identifiesAsDoctor}
          initialValues={supportForm.initialValues}
        />
      )}
    </VStack>
  );
}
